import { z } from 'zod';
import type { TFunction } from 'i18next';
import { REMINDER_DEFAULTS } from '../constants/reminderDefaults';

type ConfigurationData = {
  numberOfDays: number;
  validityInMinutes: number;
  reminderCount: number;
  reminderDelayMinutes: number;
  reminderText: string;
};

// TODO : put useForm in a hook and use it in the form

// TODO : merge with the DTO
export const createSchedulingFormSchema = (t: TFunction) => {
  // Create form-specific schema with coercion and defaults
  const formConfigurationSchema = z
    .object({
      numberOfDays: z.coerce.number().int().positive(),
      validityInMinutes: z.coerce.number().int().positive(),
      reminderCount: z.coerce
        .number()
        .int()
        .min(0)
        .max(10)
        .default(REMINDER_DEFAULTS.reminderCount),
      reminderDelayMinutes: z.coerce
        .number()
        .int()
        .min(5)
        .max(1440)
        .default(REMINDER_DEFAULTS.reminderDelayMinutes),
      reminderText: z.string().max(60).default(REMINDER_DEFAULTS.reminderText),
    })
    .superRefine((data: ConfigurationData, ctx: z.RefinementCtx) => {
      // Apply the context-specific validation with the form's validityInMinutes
      if (data.reminderCount && data.reminderCount > 0) {
        if (!data.reminderDelayMinutes) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'pages.questionnaireScheduling.configuration.reminders.delayMinutes.required',
            ),
            path: ['reminderDelayMinutes'],
          });
        }

        if (!data.reminderText) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'pages.questionnaireScheduling.configuration.reminders.text.required',
            ),
            path: ['reminderText'],
          });
        }

        if (
          data.reminderDelayMinutes &&
          data.reminderDelayMinutes >= data.validityInMinutes
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'pages.questionnaireScheduling.configuration.reminders.validation.delayTooLong',
            ),
            path: ['reminderDelayMinutes'],
          });
        }

        if (
          data.reminderDelayMinutes &&
          data.reminderCount * data.reminderDelayMinutes >=
            data.validityInMinutes
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'pages.questionnaireScheduling.configuration.reminders.validation.totalDurationTooLong',
            ),
            path: ['reminderCount'],
          });
        }
      }
    });

  return z.object({
    schedulings: z.array(z.any()),
    configuration: formConfigurationSchema,
  });
};

export type SchedulingFormSchema = ReturnType<
  typeof createSchedulingFormSchema
>;
export type SchedulingFormData = z.infer<SchedulingFormSchema>;
